// --------------------------------------------------------------
// Created On: 2024-04-01
// Author: Hannah Vaughan
//
// Last Modified: 2025-11-12
// Modified By: Zachary Thomas
//
// Copyright 2024 - 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useMemo } from "react";
import styles from "./PumpInfo.module.scss";

// Component containing a table of a pump's information on the Scan QR Code page.
export default function PumpInfo(props: Props): Component {
  const formattedDate = useMemo(() => formatDate(props.estimatedShipDate), [props.estimatedShipDate]);

  // Formats a date in MM/DD/YYYY format.
  function formatDate(unformattedDate: string | null): string {
    if (unformattedDate === null) {
      return "N/A";
    } else {
      const date = new Date(unformattedDate);
      const formattedDate = `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
      return formattedDate;
    }
  }

  return (
    <table className={`${styles.pumpInfoTable} table table-bordered`} data-test="pump-info-table">
      <tbody>
        <tr>
          <th className={styles.tableRow} scope="row">
            Serial Number:
          </th>
          <td className={styles.tableRow} data-test="serial-number-cell">
            {props.serialNumber}
          </td>
        </tr>
        <tr>
          <th className={styles.tableRow} scope="row">
            Co-Pilot ID:
          </th>
          <td className={styles.tableRow} data-test="copilot-id-cell">
            {props.copilotId !== null ? props.copilotId : "N/A"}
          </td>
        </tr>
        <tr>
          <th className={styles.tableRow} scope="row">
            BOM Number:
          </th>
          <td className={styles.tableRow} data-test="bom-number-cell">
            {props.bomNumber}
          </td>
        </tr>
        <tr>
          <th className={styles.tableRow} scope="row">
            Description:
          </th>
          <td className={styles.tableRow} data-test="pump-description-cell">
            {props.description}
          </td>
        </tr>
        <tr>
          <th className={styles.tableRow} scope="row">
            Pump Type:
          </th>
          <td className={styles.tableRow} data-test="pump-type-cell">
            {props.pumpType}
          </td>
        </tr>
        <tr>
          <th className={styles.tableRow} scope="row">
            Impeller Trim:
          </th>
          <td className={styles.tableRow} data-test="impeller-trim-cell">
            {props.impellerTrim !== null ? `${props.impellerTrim.toFixed(3)} IN` : "N/A"}
          </td>
        </tr>
        <tr>
          <th className={styles.tableRow} scope="row">
            Impeller Trim Angle:
          </th>
          <td className={styles.tableRow} data-test="impeller-trim-angle-cell">
            {props.impellerTrimAngle !== null ? `${props.impellerTrimAngle.toFixed(3)} DEG` : "N/A"}
          </td>
        </tr>
        <tr>
          <th className={styles.tableRow} scope="row">
            Estimated Ship Date:
          </th>
          <td className={styles.tableRow} data-test="estimated-ship-date-cell">
            {formattedDate}
          </td>
        </tr>
      </tbody>
    </table>
  );
}

interface Props {
  serialNumber: string;
  copilotId: number | null;
  bomNumber: string;
  description: string;
  pumpType: string;
  impellerTrim: number | null;
  impellerTrimAngle: number | null;
  estimatedShipDate: string | null;
}
